import {useEffect, useReducer} from "react";

import {
    defaultInitialState,
    notificationReducer,
    NotificationStateContext,
    NotificationStreamListener,
    type NotificationState,
} from "@khan/guide-notifications";
import {DefaultGuideState, useGuideState} from "@khan/guide-state";

import {ClientEventListener} from "./client-event-listener.tsx";

interface Props {
    /**
     * The children to be rendered within the provider and provisioned with
     * notification context.
     */
    children: React.ReactNode;
    /**
     * The initial state of the notification manager. Useful for testing.
     */
    initialState?: NotificationState;
}

/**
 * Manages Khanmigo notifications and provisions child components with their
 * context.
 */
export const NotificationStateProvider = (props: Props): React.ReactNode => {
    const {children, initialState = defaultInitialState} = props;
    const [state, dispatch] = useReducer(notificationReducer, initialState);

    const open = useGuideState("open");
    const {id: navigationId, assessmentItemId} = useGuideState("navigation");

    // Handles changes in reaction to the guide's chat window being opened or
    // closed.
    useEffect(() => {
        // If the chat window is open for any reason, we want to dismiss the
        // notifications.
        if (state.isShowing && open) {
            dispatch({type: "HIDE"});
        }
        // We only fire this effect when there are changes in the chat window
        // open state.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting over from old code
    }, [open]);

    // Handles changes in reaction to the guide's navigation metadata.
    // When the navigation ID changes, it means that the user has navigated to
    // a new page or refreshed the current page. If the assessmentItemId
    // changes, it means that the user progressed to a new assessment item
    // within a session. In either case, we want to clear the notification.
    useEffect(() => {
        // Don't hide on initial render (navigationId is the default value)
        if (
            state.isShowing &&
            DefaultGuideState.navigation.id !== navigationId
        ) {
            dispatch({type: "HIDE"});
        }
        // We only fire this effect when there are navigation changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting over from old code
    }, [navigationId, assessmentItemId]);

    return (
        <>
            <ClientEventListener />
            <NotificationStreamListener dispatch={dispatch} />
            <NotificationStateContext.Provider value={state}>
                {children}
            </NotificationStateContext.Provider>
        </>
    );
};
